import { toast } from 'react-toastify';

import axios from 'axios';
import {
  CHAINS,
  DEFAULT_CHAIN_ID,
  initialRequestState,
  LOCAL_STORAGE_KEYS,
  SLIPPAGE_GRADES,
  WETH_SUPPORTED_CHAINS,
  WOWMAX_API_URL,
} from 'consts';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { RequestState } from 'types';
import { WowmaxQuoteResponse } from 'types/aggregation';
import { bn } from 'utils/BigNumber';
import { setPartialDataToObject } from 'utils/helpers';
import { Address } from 'viem';

import { CommonStore } from '../common/CommonStore';
import { Web3Store } from '../web3/Web3Store';

import { INITIAL_SETTINGS, INITIAL_TOKENS } from './consts';

const UPDATE_SEC: Record<number, number> = {
  [CHAINS.ethereum.id]: 12,
  [CHAINS.bsc.id]: 10,
  [CHAINS.cronos.id]: 7,
  [CHAINS.base.id]: 10,
  [CHAINS.linea.id]: 12,
  [CHAINS.scroll.id]: 6,
  [CHAINS.manta.id]: 10,
  [CHAINS.blast.id]: 6,
  [CHAINS.metis.id]: 6,
  [CHAINS.xLayer.id]: 6,
  [CHAINS.arbitrum.id]: 6,
  [CHAINS.zetachain.id]: 6,
  [CHAINS.polygon.id]: 6,
  [CHAINS.soneium.id]: 6,
  [CHAINS.unit0.id]: 6,
  [CHAINS.hyper.id]: 6,
  [CHAINS.zeroG.id]: 6,
  [CHAINS.devnet.id]: 10,
};

const DEFAULT_UPDATE_SEC = 10;

export class SwapStore {
  value = '';
  tokenInAddress: Address | null = INITIAL_TOKENS[DEFAULT_CHAIN_ID]?.in || null;
  tokenOutAddress: Address | null = INITIAL_TOKENS[DEFAULT_CHAIN_ID]?.out || null;

  wowmaxResult: RequestState<WowmaxQuoteResponse> = initialRequestState;
  abortController: AbortController | null = null;
  private timerId: ReturnType<typeof setTimeout> | null = null;

  isSwapping = false;
  isApproving = false;

  settings = INITIAL_SETTINGS;
  tokenSelectField: 'in' | 'out' = 'in';
  isTokenSelectOpen = false;
  isRoutingOpen = false;
  isSettingsOpen = false;

  constructor(
    private web3: Web3Store,
    private commonStore: CommonStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    if (typeof window !== 'undefined') {
      try {
        const settingsStr = String(localStorage.getItem(LOCAL_STORAGE_KEYS.SWAP_SETTINGS));
        const settings = JSON.parse(settingsStr);
        const validSettings = this.validateSettings(settings);
        this.setSettings(validSettings);
      } catch {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SWAP_SETTINGS);
      }
    }

    reaction(
      () => this.web3.chainId,
      () => {
        this.handleChainChange();
      }
    );

    reaction(
      () => this.commonStore.mode,
      () => {
        if (this.commonStore.mode !== 'swap') {
          this.setValue('');
        }
      }
    );

    reaction(
      () => ({
        value: this.value,
        tokenIn: this.tokenIn,
        tokenOut: this.tokenOut,
      }),
      () => {
        this.fetchWowmax();
      }
    );

    reaction(
      () => ({
        isSwapping: this.isSwapping,
        isApproving: this.isApproving,
      }),
      () => {
        if (!this.isSwapping && !this.isApproving) {
          this.fetchWowmax(true);
        }
      }
    );
  }

  get tokenIn() {
    if (!this.tokenInAddress) return null;
    const token = this.commonStore.tokens.find((t) => t.address === this.tokenInAddress) || null;
    if (!token) this.tokenInAddress = null;
    return token;
  }

  get tokenOut() {
    if (!this.tokenOutAddress) return null;
    const token = this.commonStore.tokens.find((t) => t.address === this.tokenOutAddress) || null;
    if (!token) this.tokenOutAddress = null;
    return token;
  }

  setValue = (value: string) => {
    const isValueChanged = !bn(this.value).eq(value);
    if (isValueChanged) {
      this.value = value;
    }
  };

  selectToken = (field: 'in' | 'out', address: Address | null) => {
    if (field === 'in') {
      if (address && this.tokenOutAddress === address) {
        this.flipTokens();
        return;
      }
      this.tokenInAddress = address;
    } else {
      if (address && this.tokenInAddress === address) {
        this.flipTokens();
        return;
      }
      this.tokenOutAddress = address;
    }
  };

  flipTokens = () => {
    const amountOutView = this.amountOutView;
    const temp =
      this.tokenInAddress === this.web3.wrappedNativeToken?.address &&
      !WETH_SUPPORTED_CHAINS.includes(this.web3.chainId)
        ? this.web3.nativeToken?.address
        : this.tokenInAddress;
    this.tokenInAddress = this.tokenOutAddress;
    this.tokenOutAddress = temp;
    if (amountOutView) {
      const value = bn(amountOutView.replaceAll(' ', ''));
      if (!value.isNaN()) {
        this.value = value.toString();
      } else {
        this.value = '';
      }
    }
  };

  handleChainChange = () => {
    this.resetWowmax();
    this.tokenInAddress = INITIAL_TOKENS[this.web3.chainId]?.in || null;
    this.tokenOutAddress = INITIAL_TOKENS[this.web3.chainId]?.out || null;
    this.value = '';
  };

  setWowmax = (state: Partial<RequestState<WowmaxQuoteResponse>>) => {
    setPartialDataToObject(this.wowmaxResult, state);
  };

  resetWowmax = () => {
    this.abortController?.abort();
    clearTimeout(this.timerId || undefined);
    this.wowmaxResult = initialRequestState;
  };

  get isNoValue() {
    return !this.value || +this.value === 0 || bn(this.value).isNaN();
  }

  get areFormInputsValid() {
    return !this.isNoValue && !!this.tokenIn && !!this.tokenOut;
  }

  get canFetchWowmax() {
    const isWrapUnwrapFirstCall =
      this.isWrapUnwrap && !this.commonStore.prices[this.web3.nativeToken?.address];
    return (
      this.areFormInputsValid &&
      !this.web3.isDevnet &&
      (!this.isWrapUnwrap || isWrapUnwrapFirstCall) &&
      !this.isApproving &&
      !this.isSwapping
    );
  }

  fetchWowmax = async (isUpdating?: boolean) => {
    this.abortController?.abort();
    clearTimeout(this.timerId || undefined);
    this.setWowmax({ isLoading: false, isError: false });
    if (!isUpdating) this.setWowmax({ data: null });
    if (!this.canFetchWowmax) return;

    document.removeEventListener('visibilitychange', this.fetchWowmax as () => void, false);
    if (!this.commonStore.isBrowserTabActive) {
      document.addEventListener('visibilitychange', this.fetchWowmax as () => void, false);
      return;
    }

    try {
      this.setWowmax({ isLoading: true });
      runInAction(() => {
        this.abortController = new AbortController();
      });
      const chainId = this.web3.chainId;

      const response = await axios.get<WowmaxQuoteResponse>(
        `${WOWMAX_API_URL}/chains/${chainId}/quote`,
        {
          params: {
            amount: this.value,
            from: this.tokenIn?.address,
            to: this.tokenOut?.address,
            usePMM: this.settings.usePMM,
            source: 'wowmax',
          },
          signal: this.abortController?.signal,
        }
      );
      const data = response.data;
      if ((data.from as any).priceUSD === 'null') data.from.priceUSD = null;
      if ((data.to as any).priceUSD === 'null') data.to.priceUSD = null;

      this.setWowmax({ data, isLoading: false });
      this.commonStore.updatePrice(
        chainId,
        data.from.address,
        data.from.priceUSD?.toString() || null
      );
      this.commonStore.updatePrice(chainId, data.to.address, data.to.priceUSD?.toString() || null);
      console.log('wowmax', data);

      runInAction(() => {
        if (this.isWrapUnwrap) return;
        this.timerId = setTimeout(
          () => this.fetchWowmax(true),
          (UPDATE_SEC[chainId] || DEFAULT_UPDATE_SEC) * 1000
        );
      });
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request canceled: wowmax');
        return;
      }
      console.error(err);
      toast.error('An error has occurred. The server is too busy, please wait.');
      this.setWowmax({ isError: true, isLoading: false, data: null });
    }
  };

  validateSettings = (settings: unknown) => {
    const newSettings: typeof INITIAL_SETTINGS = { ...this.settings };
    if (typeof settings !== 'object' || settings === null) {
      return newSettings;
    }
    if ('usePMM' in settings) {
      if (typeof settings.usePMM === 'boolean') {
        newSettings.usePMM = settings.usePMM;
      }
    }
    if ('slippage' in settings && settings.slippage !== '') {
      const slippage = Number(settings.slippage);
      if (!Number.isNaN(slippage)) {
        newSettings.slippage = Math.max(0, Math.min(+slippage.toFixed(2), SLIPPAGE_GRADES.max));
      }
    }
    return newSettings;
  };

  setSettings = (settings: Partial<typeof INITIAL_SETTINGS>) => {
    const newSettings = { ...this.settings, ...settings };
    this.settings = newSettings;
    localStorage.setItem(LOCAL_STORAGE_KEYS.SWAP_SETTINGS, JSON.stringify(newSettings));
  };

  get hasSettingsWarning() {
    return (
      this.settings.slippage > SLIPPAGE_GRADES.tooHight ||
      this.settings.slippage < SLIPPAGE_GRADES.tooSmall
    );
  }

  get updateTimeSec() {
    return UPDATE_SEC[this.web3.chainId] || DEFAULT_UPDATE_SEC;
  }

  get amountOut() {
    if (this.isWrapUnwrap) {
      if (this.isNoValue || !this.tokenIn) return null;
      return bn(this.value).toDecimals(this.tokenIn.decimals).toString();
    }
    return this.wowmaxResult.data ? this.wowmaxResult.data.amountOut : null;
  }

  get amountOutView() {
    if (!this.amountOut || !this.tokenOut) return null;
    let decimals: number | undefined = undefined;
    const price = +this.commonStore.prices[this.tokenOut.address];

    if (price && price >= 0.01) {
      const priceInCents = price * 100;
      decimals = Math.ceil(Math.log10(priceInCents));
    }
    return bn(this.amountOut).toView(this.tokenOut.decimals, {
      signs: 10,
      toFixed: decimals || 8,
    });
  }

  get hasPMMInRoutes() {
    if (!this.wowmaxResult.data) return false;
    return this.wowmaxResult.data.routes.some((route) =>
      route.swaps.some((swap) => swap.market.name === 'HASHFLOW')
    );
  }

  get inputInDollars() {
    if (!this.value || this.isNoValue || !this.tokenIn) {
      return null;
    }
    const price = this.commonStore.prices[this.tokenIn.address];
    if (!price) return null;

    const valueInDollars = bn(this.value).mul(price).toFixed(2, bn.ROUND_DOWN);
    return valueInDollars;
  }

  get outputInDollars() {
    if (!this.tokenOut) return null;
    const price = this.commonStore.prices[this.tokenOut.address];
    if (!price || !this.amountOut) return null;

    const valueInDollars = bn(this.amountOut)
      .fromDecimals(this.tokenOut.decimals)
      .mul(price)
      .toFixed(2, bn.ROUND_DOWN);
    return valueInDollars;
  }

  get isWrapUnwrap() {
    return (
      [this.tokenInAddress, this.tokenOutAddress].includes(this.web3.nativeToken?.address) &&
      [this.tokenInAddress, this.tokenOutAddress].includes(this.web3.wrappedNativeToken?.address)
    );
  }

  setIsSwapping = (isSwapping: boolean) => {
    this.isSwapping = isSwapping;
  };

  setIsApproving = (isApproving: boolean) => {
    this.isApproving = isApproving;
  };

  openTokenSelect = (field: typeof this.tokenSelectField) => {
    this.tokenSelectField = field;
    this.isTokenSelectOpen = true;
  };

  closeTokenSelect = () => {
    this.isTokenSelectOpen = false;
  };

  openRouting = () => {
    this.isRoutingOpen = true;
  };

  closeRouting = () => {
    this.isRoutingOpen = false;
  };

  openSettings = () => {
    this.isSettingsOpen = true;
  };

  closeSettings = () => {
    this.isSettingsOpen = false;
  };
}
